export interface ApiErrorResponse {
  code: number
  developerMessage?: string
  message: string
  [key: string]: unknown
}

/**
 * Base error class for application-specific errors returned by the API.
 */
export class AppError extends Error {
  code: number
  developerMessage?: string
  payload: ApiErrorResponse

  constructor(payload: ApiErrorResponse) {
    const { code, developerMessage, message } = payload
    super(message)
    this.code = code
    this.developerMessage = developerMessage
    this.payload = payload
  }
}

interface Toast {
  error: (title: string, options?: { description?: string }) => void
}

interface Sentry {
  captureException: (error: unknown) => void
}

/**
 * Standardized API error handler to be used across the application.
 * Handles AppError instances and falls back to a generic message.
 */
export function handleApiError(
  error: unknown,
  fallbackMessage = 'Ocorreu um erro inesperado',
  toast?: Toast,
  sentry?: Sentry,
) {
  if (error instanceof AppError) {
    if (error.developerMessage) {
      console.warn('[API Error]', error.developerMessage)
    }

    if (toast) {
      toast.error('Erro', { description: error.message || fallbackMessage })
    }

    return
  }

  if (sentry) {
    sentry.captureException(error)
  }

  console.error(error)

  if (toast) {
    toast.error('Erro', { description: fallbackMessage })
  }
}

/**
 * Returns the API-provided message for AppError instances, otherwise the fallback.
 */
export function getApiErrorMessage(error: unknown, fallbackMessage: string): string {
  if (error instanceof AppError && error.message) {
    return error.message
  }

  return fallbackMessage
}
